import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Brain, TrendingUp, Target, Sparkles, ArrowRight, PieChart, BarChart3, Wallet, Clock, RefreshCw } from 'lucide-react';

const AICalculatorPage = () => {
  const [activeCalculator, setActiveCalculator] = useState('sip');
  
  // SIP Calculator
  const [sipAmount, setSipAmount] = useState(10000);
  const [sipYears, setSipYears] = useState(10);
  const [sipReturn, setSipReturn] = useState(12);

  // Lumpsum Calculator
  const [lumpAmount, setLumpAmount] = useState(100000);
  const [lumpYears, setLumpYears] = useState(10);
  const [lumpReturn, setLumpReturn] = useState(12);

  // Goal Calculator
  const [goalAmount, setGoalAmount] = useState(1000000);
  const [goalYears, setGoalYears] = useState(10);
  const [goalReturn, setGoalReturn] = useState(12);

  // SIP Calculation
  const calculateSIP = () => {
    const P = sipAmount;
    const r = sipReturn / 100 / 12;
    const n = sipYears * 12;
    const FV = P * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
    return Math.round(FV);
  };

  const sipInvested = sipAmount * sipYears * 12;
  const sipWealth = calculateSIP();
  const sipGains = sipWealth - sipInvested;

  // Lumpsum Calculation
  const calculateLumpsum = () => {
    const P = lumpAmount;
    const r = lumpReturn / 100;
    const t = lumpYears;
    const FV = P * Math.pow(1 + r, t);
    return Math.round(FV);
  };

  const lumpWealth = calculateLumpsum();
  const lumpGains = lumpWealth - lumpAmount;

  // Goal Calculation (How much to invest monthly)
  const calculateGoalSIP = () => {
    const FV = goalAmount;
    const r = goalReturn / 100 / 12;
    const n = goalYears * 12;
    const P = FV * r / ((Math.pow(1 + r, n) - 1) * (1 + r));
    return Math.round(P);
  };

  const goalSIP = calculateGoalSIP();

  const calculators = [
    { id: 'sip', name: 'SIP Calculator', icon: TrendingUp },
    { id: 'lumpsum', name: 'Lumpsum Calculator', icon: Wallet },
    { id: 'goal', name: 'Goal Planner', icon: Target },
  ];

  const formatCurrency = (num) => {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    return `₹${num.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-purple-500/10" />
        <div className="absolute top-10 left-20 w-60 h-60 bg-cyan-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-20 w-80 h-80 bg-purple-500/15 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-400/10 border border-cyan-400/30 rounded-full mb-6">
              <Brain className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">AI-Powered Calculations</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Investment
              <span className="text-yellow-400"> Calculators</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Plan your investments smartly with our AI-powered calculators. 
              Visualize your wealth growth and achieve your financial goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Calculator Tabs */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8 sm:mb-12">
            {calculators.map((calc) => (
              <motion.button
                key={calc.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveCalculator(calc.id)}
                className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all text-sm sm:text-base ${
                  activeCalculator === calc.id
                    ? 'bg-yellow-400 text-black'
                    : 'bg-zinc-900 text-gray-300 border border-zinc-800 hover:border-yellow-400/50'
                }`}
              >
                <calc.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">{calc.name}</span>
                <span className="sm:hidden">{calc.name.split(' ')[0]}</span>
              </motion.button>
            ))}
          </div>

          {/* SIP Calculator */}
          {activeCalculator === 'sip' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid lg:grid-cols-2 gap-8"
            >
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl sm:rounded-3xl p-4 sm:p-8">
                <div className="flex items-center gap-3 mb-6 sm:mb-8">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-400/20 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white">SIP Calculator</h2>
                    <p className="text-gray-400 text-xs sm:text-sm">Calculate your SIP returns</p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-white font-medium">Monthly Investment</label>
                      <span className="text-yellow-400 font-bold">₹{sipAmount.toLocaleString()}</span>
                    </div>
                    <input
                      type="range"
                      min="1000"
                      max="100000"
                      step="1000"
                      value={sipAmount}
                      onChange={(e) => setSipAmount(Number(e.target.value))}
                      className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-yellow-400"
                    />
                    <div className="flex justify-between text-gray-500 text-sm mt-1">
                      <span>₹1,000</span>
                      <span>₹1,00,000</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-white font-medium">Investment Period</label>
                      <span className="text-yellow-400 font-bold">{sipYears} Years</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      value={sipYears}
                      onChange={(e) => setSipYears(Number(e.target.value))}
                      className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-yellow-400"
                    />
                    <div className="flex justify-between text-gray-500 text-sm mt-1">
                      <span>1 Year</span>
                      <span>30 Years</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-white font-medium">Expected Return</label>
                      <span className="text-green-400 font-bold">{sipReturn}% p.a.</span>
                    </div>
                    <input
                      type="range"
                      min="6"
                      max="20"
                      step="0.5"
                      value={sipReturn}
                      onChange={(e) => setSipReturn(Number(e.target.value))}
                      className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-green-400"
                    />
                    <div className="flex justify-between text-gray-500 text-sm mt-1">
                      <span>6%</span>
                      <span>20%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8">
                <h3 className="text-xl font-bold text-white mb-8 text-center">Your SIP Results</h3>
                
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center p-4 bg-black/50 rounded-xl">
                    <span className="text-gray-400">Invested Amount</span>
                    <span className="text-white font-bold text-xl">{formatCurrency(sipInvested)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-black/50 rounded-xl">
                    <span className="text-gray-400">Est. Returns</span>
                    <span className="text-green-400 font-bold text-xl">{formatCurrency(sipGains)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-yellow-400/10 border border-yellow-400/30 rounded-xl">
                    <span className="text-yellow-400 font-medium">Total Wealth</span>
                    <span className="text-yellow-400 font-bold text-2xl">{formatCurrency(sipWealth)}</span>
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="relative h-8 bg-zinc-800 rounded-full overflow-hidden mb-4">
                  <div 
                    className="absolute left-0 top-0 h-full bg-blue-500 rounded-l-full"
                    style={{ width: `${(sipInvested / sipWealth) * 100}%` }}
                  />
                  <div 
                    className="absolute top-0 h-full bg-green-500 rounded-r-full"
                    style={{ left: `${(sipInvested / sipWealth) * 100}%`, width: `${(sipGains / sipWealth) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                    <span className="text-gray-400">Invested</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                    <span className="text-gray-400">Returns</span>
                  </span>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full mt-8 px-8 py-4 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition-all"
                >
                  Start This SIP <ArrowRight className="inline ml-2 w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Lumpsum Calculator */}
          {activeCalculator === 'lumpsum' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid lg:grid-cols-2 gap-8"
            >
              <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-purple-400/20 rounded-xl flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Lumpsum Calculator</h2>
                    <p className="text-gray-400 text-sm">Calculate one-time investment returns</p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-white font-medium">Investment Amount</label>
                      <span className="text-yellow-400 font-bold">₹{lumpAmount.toLocaleString()}</span>
                    </div>
                    <input
                      type="range"
                      min="10000"
                      max="1000000"
                      step="10000"
                      value={lumpAmount}
                      onChange={(e) => setLumpAmount(Number(e.target.value))}
                      className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-yellow-400"
                    />
                    <div className="flex justify-between text-gray-500 text-sm mt-1">
                      <span>₹10,000</span>
                      <span>₹10,00,000</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-white font-medium">Investment Period</label>
                      <span className="text-yellow-400 font-bold">{lumpYears} Years</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      value={lumpYears}
                      onChange={(e) => setLumpYears(Number(e.target.value))}
                      className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-yellow-400"
                    />
                    <div className="flex justify-between text-gray-500 text-sm mt-1">
                      <span>1 Year</span>
                      <span>30 Years</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-white font-medium">Expected Return</label>
                      <span className="text-green-400 font-bold">{lumpReturn}% p.a.</span>
                    </div>
                    <input
                      type="range"
                      min="6"
                      max="20"
                      step="0.5"
                      value={lumpReturn}
                      onChange={(e) => setLumpReturn(Number(e.target.value))}
                      className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-green-400"
                    />
                    <div className="flex justify-between text-gray-500 text-sm mt-1">
                      <span>6%</span>
                      <span>20%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8">
                <h3 className="text-xl font-bold text-white mb-8 text-center">Your Lumpsum Results</h3>
                
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center p-4 bg-black/50 rounded-xl">
                    <span className="text-gray-400">Invested Amount</span>
                    <span className="text-white font-bold text-xl">{formatCurrency(lumpAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-black/50 rounded-xl">
                    <span className="text-gray-400">Est. Returns</span>
                    <span className="text-green-400 font-bold text-xl">{formatCurrency(lumpGains)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-yellow-400/10 border border-yellow-400/30 rounded-xl">
                    <span className="text-yellow-400 font-medium">Total Wealth</span>
                    <span className="text-yellow-400 font-bold text-2xl">{formatCurrency(lumpWealth)}</span>
                  </div>
                </div>

                <div className="p-4 bg-purple-400/10 border border-purple-400/30 rounded-xl mb-6">
                  <div className="flex items-center gap-2 text-purple-400 text-sm">
                    <Sparkles className="w-4 h-4" />
                    <span>Your money will grow <strong>{(lumpWealth / lumpAmount).toFixed(1)}x</strong> in {lumpYears} years!</span>
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full px-8 py-4 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition-all"
                >
                  Invest Now <ArrowRight className="inline ml-2 w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Goal Calculator */}
          {activeCalculator === 'goal' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid lg:grid-cols-2 gap-8"
            >
              <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-cyan-400/20 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Goal Planner</h2>
                    <p className="text-gray-400 text-sm">Plan how to reach your financial goal</p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-white font-medium">Your Goal Amount</label>
                      <span className="text-yellow-400 font-bold">{formatCurrency(goalAmount)}</span>
                    </div>
                    <input
                      type="range"
                      min="100000"
                      max="10000000"
                      step="100000"
                      value={goalAmount}
                      onChange={(e) => setGoalAmount(Number(e.target.value))}
                      className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-yellow-400"
                    />
                    <div className="flex justify-between text-gray-500 text-sm mt-1">
                      <span>₹1 Lakh</span>
                      <span>₹1 Crore</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-white font-medium">Time to Achieve</label>
                      <span className="text-yellow-400 font-bold">{goalYears} Years</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      value={goalYears}
                      onChange={(e) => setGoalYears(Number(e.target.value))}
                      className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-yellow-400"
                    />
                    <div className="flex justify-between text-gray-500 text-sm mt-1">
                      <span>1 Year</span>
                      <span>30 Years</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-white font-medium">Expected Return</label>
                      <span className="text-green-400 font-bold">{goalReturn}% p.a.</span>
                    </div>
                    <input
                      type="range"
                      min="6"
                      max="20"
                      step="0.5"
                      value={goalReturn}
                      onChange={(e) => setGoalReturn(Number(e.target.value))}
                      className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-green-400"
                    />
                    <div className="flex justify-between text-gray-500 text-sm mt-1">
                      <span>6%</span>
                      <span>20%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8">
                <h3 className="text-xl font-bold text-white mb-8 text-center">Your Investment Plan</h3>
                
                <div className="text-center mb-8">
                  <div className="w-32 h-32 bg-gradient-to-br from-cyan-400/20 to-purple-400/20 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-cyan-400/50">
                    <div>
                      <div className="text-3xl font-bold text-white">{formatCurrency(goalSIP)}</div>
                      <div className="text-cyan-400 text-sm">per month</div>
                    </div>
                  </div>
                  <p className="text-gray-400">
                    Invest <span className="text-yellow-400 font-bold">{formatCurrency(goalSIP)}</span> monthly to reach your goal of <span className="text-yellow-400 font-bold">{formatCurrency(goalAmount)}</span>
                  </p>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center p-4 bg-black/50 rounded-xl">
                    <span className="text-gray-400">Total Investment</span>
                    <span className="text-white font-bold">{formatCurrency(goalSIP * goalYears * 12)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-black/50 rounded-xl">
                    <span className="text-gray-400">Returns Earned</span>
                    <span className="text-green-400 font-bold">{formatCurrency(goalAmount - (goalSIP * goalYears * 12))}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-cyan-400/10 border border-cyan-400/30 rounded-xl">
                    <span className="text-cyan-400 font-medium">Goal Amount</span>
                    <span className="text-cyan-400 font-bold text-xl">{formatCurrency(goalAmount)}</span>
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full px-8 py-4 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition-all"
                >
                  Start Investing <ArrowRight className="inline ml-2 w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-20 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Smart Investment Tips
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Clock, title: "Start Early", desc: "The power of compounding works best when you start early. Even small amounts grow significantly over time." },
              { icon: RefreshCw, title: "Stay Consistent", desc: "Regular investments through SIP help average out market volatility and build discipline." },
              { icon: PieChart, title: "Diversify", desc: "Spread your investments across different asset classes to minimize risk and maximize returns." }
            ].map((tip, index) => (
              <motion.div
                key={tip.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-black/50 border border-zinc-800 rounded-2xl p-6 text-center"
              >
                <div className="w-14 h-14 bg-yellow-400/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <tip.icon className="w-7 h-7 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{tip.title}</h3>
                <p className="text-gray-400 text-sm">{tip.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-cyan-500/20 to-purple-500/10 border border-cyan-400/30 rounded-3xl p-12 text-center"
          >
            <Brain className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Grow Your Wealth?
            </h2>
            <p className="text-gray-300 mb-8 max-w-xl mx-auto">
              Use our AI-powered tools to plan your investments and achieve your financial dreams. 
              Start your journey today!
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-4 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition-all shadow-lg shadow-yellow-400/25"
            >
              Start Investing Now <ArrowRight className="inline ml-2 w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AICalculatorPage;
